import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Delete as DeleteIcon,
  Add as AddIcon,
  CloudUpload,
  Print,
  TextSnippet
} from '@mui/icons-material';
import { format } from 'date-fns';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { orderService } from '../services/orderService';
import { vendorService } from '../services/vendorService';
import { employeeService } from '../services/employeeService';
import { attachmentService } from '../services/attachmentService';
import {
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_FILE_SIZE_LABEL,
} from '../constants/attachmentLimits';
import AttachmentCategoryChips from '../components/AttachmentCategoryChips';
import { useAuth } from '../hooks/useAuth';
import { useDocumentTabs } from '../context/DocumentTabsContext';
import {
  ProcurementSummaryBar,
  ProcurementSummaryField,
  ProcurementSummaryNotesDense,
  PoReceiptStatusChip,
  getPoReceiptStatusChipProps,
  ShipmentsReceivedSummaryLinks
} from '../components/procurement/ProcurementSummaryBar';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import '../styles/print.css';
import { formatCurrency } from '../components/CurrencyTextField';
import { computePOLineExtended, computePOLinesSubtotal } from '../utils/purchaseOrderLines';
import { formatDateOnly, formatLocalTodayYmd } from '../utils/dateUtils';

const SHIP_VIA_OPTIONS = ['UPS', 'USPS', 'DHL', 'FedEx', 'In-house carrier'];

/** Receipt / workflow statuses editable on saved (non-draft) PO detail. */
const PO_DETAIL_EDITABLE_STATUSES = ['ORDERED', 'PARTIALLY RCVD', 'RECEIVED IN FULL'];

function normPoStatus(s) {
  return String(s || '')
    .trim()
    .toUpperCase();
}
const NEW_PO_DRAFT_STORAGE_KEY = 'wm_new_purchase_order_draft_v1';
const PO_BOOTSTRAP_SESSION_PREFIX = 'wm_po_bootstrap_';

function buildPoFormAndLinesFromPOData(data) {
  if (!data) return null;
  const nextForm = {
    po_number: data?.identifier ?? data?.po_number ?? '',
    vendor_id: data?.vendor_id ?? '',
    vendor_name: data?.vendor_name ?? '',
    ordered_by_id: data?.ordered_by_id ?? '',
    work_order_id: data?.work_order_id ?? '',
    order_date: (data?.order_date || '').toString().slice(0, 10) || formatLocalTodayYmd(),
    expected_date: (data?.expected_date || '').toString().slice(0, 10) || '',
    ship_via: data?.ship_via ?? '',
    notes: data?.notes ?? '',
    status: normPoStatus(data?.status) || 'ORDERED'
  };
  const nextLines =
    (data?.items || []).length > 0
      ? data.items.map((it, idx) => ({
          id: it.id,
          line_number: it.line_number ?? idx + 1,
          part_number: it.part_number ?? '',
          description: it.description ?? it.part_description ?? '',
          quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
          unit: it.unit ?? 'EA',
          unit_price: it.unit_price ?? '',
          notes: it.notes ?? '',
          work_order_id: it.work_order_id ?? null,
          work_order_item_id: it.work_order_item_id ?? null
        }))
      : [emptyLineItem()];
  return { nextForm, nextLines };
}

/** First paint after POST /new → /:id: hydrate from router state or session backup (Strict Mode safe). */
function readBootstrapPurchaseOrder(id, loc) {
  if (!id || id === 'new') return null;
  try {
    const fromNav = loc?.state?.wmNewPoBootstrap;
    if (fromNav && String(fromNav.id) === String(id)) return fromNav;
    const raw = sessionStorage.getItem(`${PO_BOOTSTRAP_SESSION_PREFIX}${id}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && String(parsed.id) === String(id) ? parsed : null;
  } catch {
    return null;
  }
}

function persistPoBootstrapSession(newId, poData) {
  if (!newId || !poData) return;
  try {
    sessionStorage.setItem(`${PO_BOOTSTRAP_SESSION_PREFIX}${newId}`, JSON.stringify(poData));
  } catch {
    /* ignore */
  }
}

function clearPoBootstrapSession(newId) {
  if (!newId) return;
  try {
    sessionStorage.removeItem(`${PO_BOOTSTRAP_SESSION_PREFIX}${newId}`);
  } catch {
    /* ignore */
  }
}

const emptyPOForm = () => ({
  po_number: '',
  vendor_id: '',
  vendor_name: '',
  ordered_by_id: '',
  work_order_id: '',
  order_date: formatLocalTodayYmd(),
  expected_date: '',
  ship_via: '',
  notes: '',
  status: 'ORDERED'
});

const emptyLineItem = () => ({
  line_number: 1,
  part_id: null,
  part_number: '',
  description: '',
  quantity_ordered: 0,
  unit: 'EA',
  unit_price: '',
  notes: '',
  work_order_id: null,
  work_order_item_id: null
});

/** Stable JSON for comparing new-PO form state to last local draft (tab dirty guard). */
function serializeNewPODraftState(poForm, lineItems) {
  const normLine = (it, idx) => ({
    line_number: it.line_number ?? idx + 1,
    part_id: it.part_id ?? null,
    part_number: it.part_number ?? '',
    description: it.description ?? '',
    quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
    unit: it.unit ?? 'EA',
    unit_price: it.unit_price ?? '',
    notes: it.notes ?? '',
    work_order_id: it.work_order_id ?? null,
    work_order_item_id: it.work_order_item_id ?? null
  });
  const lines = (Array.isArray(lineItems) ? lineItems : []).map(normLine);
  const form = {
    po_number: poForm.po_number ?? '',
    vendor_id: poForm.vendor_id ?? '',
    vendor_name: poForm.vendor_name ?? '',
    ordered_by_id: poForm.ordered_by_id ?? '',
    work_order_id: poForm.work_order_id ?? '',
    order_date: poForm.order_date ?? '',
    expected_date: poForm.expected_date ?? '',
    ship_via: poForm.ship_via ?? '',
    notes: poForm.notes ?? '',
    status: normPoStatus(poForm.status) || 'ORDERED'
  };
  return JSON.stringify({ poForm: form, lineItems: lines });
}

/** True when we should POST/PUT a server draft (not only localStorage). */
function newPODraftHasServerPayload(poForm, lineItems) {
  if (!poForm) return false;
  if (poForm.vendor_id) return true;
  if (poForm.work_order_id) return true;
  if (String(poForm.notes || '').trim()) return true;
  if (
    Array.isArray(lineItems) &&
    lineItems.some(
      (it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0
    )
  )
    return true;
  return false;
}

const ProcurementPODetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const printRequested = searchParams.get('print') === '1';
  const hasAutoPrintedRef = useRef(false);
  // `/procurement/po/new` is a static route (no `:id` param), so `id` can be undefined.
  // Detect "new" via pathname so we don't call `/api/purchase-orders/undefined` on mount.
  const isNew = id === 'new' || location.pathname.endsWith('/new');
  const prefillWorkOrderId = searchParams.get('work_order_id');
  const prefillLineItemIds = String(searchParams.get('line_item_ids') || '')
    .split(',')
    .map((v) => parseInt(v, 10))
    .filter((n) => Number.isFinite(n));

  const navBootstrapPo = readBootstrapPurchaseOrder(id, location);
  const navHydrated = navBootstrapPo ? buildPoFormAndLinesFromPOData(navBootstrapPo) : null;

  const [loading, setLoading] = useState(() => (isNew ? false : !navBootstrapPo));
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [error, setError] = useState('');
  const [po, setPo] = useState(() => navBootstrapPo ?? null);
  const [poForm, setPoForm] = useState(() => navHydrated?.nextForm ?? emptyPOForm());
  const [lineItems, setLineItems] = useState(() => navHydrated?.nextLines ?? [emptyLineItem()]);
  const poFormRef = useRef(poForm);
  const lineItemsRef = useRef(lineItems);
  useEffect(() => {
    poFormRef.current = poForm;
  }, [poForm]);
  useEffect(() => {
    lineItemsRef.current = lineItems;
  }, [lineItems]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [linkedReceivers, setLinkedReceivers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [isDraggingOverAttachment, setIsDraggingOverAttachment] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [jobSearchOptions, setJobSearchOptions] = useState([]);
  const [jobSearchLoading, setJobSearchLoading] = useState(false);
  const [linkedJobRow, setLinkedJobRow] = useState(null);
  const jobSearchTimerRef = useRef(null);
  const [identifierPreview, setIdentifierPreview] = useState('');
  const { user } = useAuth();
  const { registerTabTitle, registerDirtyState, tryNavigate } = useDocumentTabs();
  const [poDirtyBaseline, setPoDirtyBaseline] = useState(() =>
    navHydrated ? JSON.stringify({ poForm: navHydrated.nextForm, lineItems: navHydrated.nextLines }) : null
  );
  const [lastPersistedDraftSnapshot, setLastPersistedDraftSnapshot] = useState(null);
  const [newPoDraftSavedVisible, setNewPoDraftSavedVisible] = useState(false);
  const newPoDraftSavedFlashTimeoutRef = useRef(null);
  const newPoBaselineCapturedRef = useRef(false);
  /** Prevents duplicate POST /purchase-orders when vendor-triggered autosave overlaps Save Draft. */
  const newPoServerDraftSavingRef = useRef(false);
  /** After first successful POST from /new, skip unmount/visibility flush to avoid duplicate creates. */
  const navigatedAwayAfterServerDraftRef = useRef(false);
  const poPrintFilename = po?.identifier || po?.po_number || 'Purchase-Order';
  const isDraftPo = !isNew && po && String(po.status || '').trim().toUpperCase() === 'DRAFT';
  const showReceiptStatusSelect =
    !isNew && !isDraftPo && normPoStatus(po?.status) !== 'CANCELLED';
  const canReceiveShipment =
    !isNew && !isDraftPo && normPoStatus(po?.status) !== 'CANCELLED';

  const poLinesForPrint = useMemo(
    () =>
      lineItems.filter(
        (it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0
      ),
    [lineItems]
  );
  const poPrintSubtotal = useMemo(() => computePOLinesSubtotal(poLinesForPrint), [poLinesForPrint]);

  /** Header work order only (purchase_orders / edited form). Not inferred from lines. */
  const poLinkedWorkOrderId = useMemo(() => {
    const fromForm = poForm.work_order_id ? Number(poForm.work_order_id) : null;
    if (Number.isFinite(fromForm) && fromForm > 0) return fromForm;
    if (!isNew && po?.work_order_id != null) {
      const p = Number(po.work_order_id);
      if (Number.isFinite(p) && p > 0) return p;
    }
    return null;
  }, [poForm.work_order_id, isNew, po?.work_order_id]);

  const previewLinkedWorkOrderId = useMemo(() => {
    if (poLinkedWorkOrderId != null) return poLinkedWorkOrderId;
    const pre = prefillWorkOrderId ? Number(prefillWorkOrderId) : null;
    if (Number.isFinite(pre) && pre > 0) return pre;
    return null;
  }, [poLinkedWorkOrderId, prefillWorkOrderId]);

  /** Fetch order row for labels (summary + banner); include URL prefill on /new before the form is filled. */
  const linkedOrderFetchId = useMemo(() => {
    if (poLinkedWorkOrderId != null) return poLinkedWorkOrderId;
    if (isNew && prefillWorkOrderId) {
      const n = Number(prefillWorkOrderId);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    return null;
  }, [poLinkedWorkOrderId, isNew, prefillWorkOrderId]);

  const isOrderLinkedPo = linkedOrderFetchId != null;

  useEffect(() => {
    const wid = linkedOrderFetchId;
    if (!wid || !Number.isFinite(wid)) {
      setLinkedJobRow(null);
      return undefined;
    }
    let cancelled = false;
    orderService
      .getById(wid)
      .then((res) => {
        if (!cancelled) setLinkedJobRow(res?.data || null);
      })
      .catch(() => {
        if (!cancelled) setLinkedJobRow(null);
      });
    return () => {
      cancelled = true;
    };
  }, [linkedOrderFetchId]);

  const orderLinkedPoTopBanner = useMemo(() => {
    if (!linkedOrderFetchId) return null;
    const phrase =
      linkedJobRow && Number(linkedJobRow.id) === linkedOrderFetchId
        ? linkedJobRow.identifier || linkedJobRow.work_order_number || `order #${linkedOrderFetchId}`
        : `order #${linkedOrderFetchId}`;
    return (
      <Alert severity="info" sx={{ py: 0.75, pr: 1, '& .MuiAlert-message': { width: '100%' } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Order-linked purchase order
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
          This PO is tied to work order <strong>{phrase}</strong>.
        </Typography>
        <Button
          component={RouterLink}
          to={`/work-orders/${linkedOrderFetchId}`}
          size="small"
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          Open order{' '}
          {linkedJobRow && Number(linkedJobRow.id) === linkedOrderFetchId
            ? linkedJobRow.identifier || linkedJobRow.work_order_number || `#${linkedOrderFetchId}`
            : `#${linkedOrderFetchId}`}
        </Button>
      </Alert>
    );
  }, [linkedOrderFetchId, linkedJobRow]);

  // For the /procurement/po/new route, default "Ordered By" to the logged-in user.
  useEffect(() => {
    if (!isNew) return;
    if (!user?.id) return;
    setPoForm((f) => (f.ordered_by_id ? f : { ...f, ordered_by_id: Number(user.id) }));
  }, [isNew, user?.id]);

  const flashNewPoDraftSaved = useCallback(() => {
    if (!isNew) return;
    if (newPoDraftSavedFlashTimeoutRef.current) clearTimeout(newPoDraftSavedFlashTimeoutRef.current);
    setNewPoDraftSavedVisible(true);
    newPoDraftSavedFlashTimeoutRef.current = setTimeout(() => {
      setNewPoDraftSavedVisible(false);
      newPoDraftSavedFlashTimeoutRef.current = null;
    }, 2200);
  }, [isNew]);

  useEffect(() => {
    return () => {
      if (newPoDraftSavedFlashTimeoutRef.current) clearTimeout(newPoDraftSavedFlashTimeoutRef.current);
    };
  }, []);

  useEffect(
    () => () => {
      if (jobSearchTimerRef.current) clearTimeout(jobSearchTimerRef.current);
    },
    []
  );

  const clearNewDraftFromStorage = useCallback(() => {
    try {
      localStorage.removeItem(NEW_PO_DRAFT_STORAGE_KEY);
    } catch {
      // ignore storage failures
    }
  }, []);

  // Legacy: remove client-only draft from the old flow so it never repopulates the form.
  useEffect(() => {
    if (!isNew) return;
    clearNewDraftFromStorage();
  }, [isNew, clearNewDraftFromStorage]);

  // Dirty-tab baseline for new P.O.: capture after default "Ordered By" is applied (see ordered_by effect below).
  useLayoutEffect(() => {
    if (!isNew) return;
    if (newPoBaselineCapturedRef.current) return;
    if (user?.id && !poForm.ordered_by_id) return;
    setLastPersistedDraftSnapshot(serializeNewPODraftState(poForm, lineItems));
    newPoBaselineCapturedRef.current = true;
  }, [isNew, user?.id, poForm, lineItems]);

  useEffect(() => {
    const doPrefill = async () => {
      if (!isNew || !prefillWorkOrderId) return;
      try {
        const res = await purchaseOrderService.prefillFromOrder(prefillWorkOrderId, prefillLineItemIds);
        const header = res?.data?.header || {};
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        const baseForm = poFormRef.current;
        const baseLines = lineItemsRef.current;
        const mappedLines =
          items.length > 0
            ? items.map((it, idx) => ({
                line_number: it.line_number ?? idx + 1,
                part_id: it.part_id ?? null,
                part_number: it.part_number ?? '',
                description: it.description ?? '',
                quantity_ordered: parseInt(it.quantity_ordered, 10) || 0,
                unit: it.unit ?? 'EA',
                unit_price: it.unit_price ?? '',
                notes: it.notes ?? '',
                work_order_id: it.work_order_id ?? null,
                work_order_item_id: it.work_order_item_id ?? null
              }))
            : null;
        const nextForm = {
          ...baseForm,
          work_order_id:
            header.work_order_id != null && header.work_order_id !== ''
              ? Number(header.work_order_id)
              : baseForm.work_order_id || (prefillWorkOrderId ? Number(prefillWorkOrderId) : ''),
          order_date: header.order_date || baseForm.order_date,
          notes: header.notes || baseForm.notes
        };
        const nextLines = mappedLines ?? baseLines;
        setPoForm(nextForm);
        setLineItems(nextLines);
        setLastPersistedDraftSnapshot(serializeNewPODraftState(nextForm, nextLines));
        newPoBaselineCapturedRef.current = true;
      } catch {
        // Non-blocking prefill
      }
    };
    doPrefill();
  }, [isNew, prefillWorkOrderId, prefillLineItemIds]);

  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await purchaseOrderService.getNextIdentifierPreview({
          order_date: poForm.order_date,
          work_order_id: previewLinkedWorkOrderId
        });
        const prev = res?.data?.identifier_preview ?? '';
        if (!cancelled) setIdentifierPreview(prev);
      } catch {
        if (!cancelled) setIdentifierPreview('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, poForm.order_date, previewLinkedWorkOrderId]);

  const loadAttachments = async (poId) => {
    if (!poId) {
      setAttachments([]);
      return;
    }
    try {
      setAttachmentsLoading(true);
      setAttachmentsError('');
      const res = await attachmentService.getForPurchaseOrder(poId);
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setAttachments(list);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to load attachments');
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const loadDetail = useCallback(
    async (opts = {}) => {
      const silent = Boolean(opts.silent);
      if (isNew) return;
      try {
        if (!silent) {
          setLoading(true);
          setPoDirtyBaseline(null);
        }
        setError('');
        const [poRes, recRes] = await Promise.all([
          purchaseOrderService.getById(id),
          purchaseOrderService.getShipmentsInForPO(id)
        ]);
        const data = poRes?.data;
        setPo(data);
        const built =
          buildPoFormAndLinesFromPOData(data) || {
            nextForm: emptyPOForm(),
            nextLines: [emptyLineItem()]
          };
        const { nextForm, nextLines } = built;
        // Silent load: right after POST draft navigates /new → /:id, bootstrap already hydrated the form.
        // Applying GET results here races user edits and resets quantities/lines (feels "uneditable").
        if (!silent) {
          setPoForm(nextForm);
          setLineItems(nextLines);
          setPoDirtyBaseline(JSON.stringify({ poForm: nextForm, lineItems: nextLines }));
        }
        setLinkedReceivers(recRes?.data ?? []);
        await loadAttachments(id);
        if (silent) clearPoBootstrapSession(id);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load purchase order');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id, isNew]
  );

  // One Route matches `/procurement/po/new` and `/procurement/po/:id` (id !== 'new'), so the first
  // server draft save only changes `useParams().id` — this layout pass hydrates bootstrap without a remount.
  useLayoutEffect(() => {
    const stripBootstrapNavState = () => {
      if (!location.state?.wmNewPoBootstrap) return;
      navigate(`${location.pathname}${location.search || ''}`, { replace: true, state: {} });
    };

    if (isNew) {
      stripBootstrapNavState();
      return;
    }

    const boot = readBootstrapPurchaseOrder(id, location);
    if (!boot || String(boot.id) !== String(id)) {
      stripBootstrapNavState();
      return;
    }

    if (po && String(po.id) === String(id)) {
      stripBootstrapNavState();
      return;
    }

    const built =
      buildPoFormAndLinesFromPOData(boot) || { nextForm: emptyPOForm(), nextLines: [emptyLineItem()] };
    setPo(boot);
    setPoForm(built.nextForm);
    setLineItems(built.nextLines);
    setPoDirtyBaseline(JSON.stringify({ poForm: built.nextForm, lineItems: built.nextLines }));
    setLoading(false);
    stripBootstrapNavState();
  }, [isNew, id, location.pathname, location.search, location.state, navigate, po]);

  useEffect(() => {
    vendorService.getAll({ limit: 500, activeOnly: false }).then((res) => setVendors(res?.data ?? [])).catch(() => {});
    employeeService.getAll({ limit: 500, activeOnly: 'true' }).then((res) => setEmployees(res?.data ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    const hasBootstrap = readBootstrapPurchaseOrder(id, location) != null;
    void loadDetail({ silent: hasBootstrap });
    // Intentionally omit `location` from deps: stripping `wmNewPoBootstrap` would otherwise rerun this
    // effect and force a second load (full `loading` gate) after the first silent reconcile.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location omitted on purpose; see above
  }, [loadDetail, id, isNew]);

  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  useEffect(() => {
    if (isNew) return;
    if (!printRequested) return;
    if (loading) return;
    if (!po) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    printWithFilename(poPrintFilename, { delayMs: 500 });
  }, [isNew, printRequested, loading, po, poPrintFilename]);

  useEffect(() => {
    if (!printRequested) {
      hasAutoPrintedRef.current = false;
    }
  }, [printRequested]);

  const addLineItem = () => setLineItems((prev) => [...prev, { ...emptyLineItem(), line_number: prev.length + 1 }]);
  const updateLineItem = (idx, field, value) => setLineItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  const removeLineItem = (idx) => {
    setLineItems((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length === 0 ? [emptyLineItem()] : next.map((it, i) => ({ ...it, line_number: i + 1 }));
    });
  };

  const normalizedItems = useCallback(
    () =>
      lineItems
        .filter((it) => (it.part_number || it.description || '').trim() || Number(it.quantity_ordered) > 0)
        .map((it, idx) => ({
          line_number: idx + 1,
          part_id: it.part_id ?? null,
          part_number: it.part_number || null,
          description: it.description || null,
          quantity_ordered: Math.max(0, parseInt(it.quantity_ordered, 10) || 0),
          unit: it.unit || 'EA',
          unit_price: it.unit_price !== '' && it.unit_price != null ? Number(it.unit_price) : null,
          notes: it.notes || null,
          work_order_id: it.work_order_id ?? null,
          work_order_item_id: it.work_order_item_id ?? null
        })),
    [lineItems]
  );

  const persistPurchaseOrder = useCallback(async () => {
    const items = normalizedItems();
    const canFinalize = Boolean(poForm.vendor_id) && items.length > 0;
    if (!isDraftPo && !isNew) {
      if (!poForm.vendor_id) {
        const msg = 'Vendor is required.';
        setError(msg);
        throw new Error(msg);
      }
      if (items.length === 0) {
        const msg = 'At least one line item is required.';
        setError(msg);
        throw new Error(msg);
      }
    } else if (isNew) {
      if (!poForm.vendor_id) {
        const msg = 'Vendor is required.';
        setError(msg);
        throw new Error(msg);
      }
      if (items.length === 0) {
        const msg = 'At least one line item is required.';
        setError(msg);
        throw new Error(msg);
      }
    }
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const res = await purchaseOrderService.create({
          vendor_id: Number(poForm.vendor_id),
          ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
          work_order_id: poForm.work_order_id ? Number(poForm.work_order_id) : null,
          order_date: poForm.order_date,
          expected_date: poForm.expected_date || null,
          ship_via: poForm.ship_via || null,
          notes: poForm.notes || null,
          items
        });
        clearNewDraftFromStorage();
        const poData = res?.data;
        const newId = poData?.id ?? res?.id;
        if (newId) {
          persistPoBootstrapSession(newId, poData);
          navigate(`/procurement/po/${newId}`, {
            replace: true,
            state: poData ? { wmNewPoBootstrap: poData } : {}
          });
        } else navigate('/procurement/po');
      } else {
        const payload = {
          po_number: poForm.po_number,
          vendor_id: poForm.vendor_id ? Number(poForm.vendor_id) : null,
          ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
          work_order_id: poForm.work_order_id ? Number(poForm.work_order_id) : null,
          order_date: poForm.order_date,
          expected_date: poForm.expected_date || null,
          ship_via: poForm.ship_via || null,
          notes: poForm.notes || null,
          items
        };
        if (isDraftPo) {
          if (canFinalize) payload.status = 'ORDERED';
        } else if (normPoStatus(po?.status) !== 'CANCELLED') {
          const fs = normPoStatus(poForm.status);
          const ps = normPoStatus(po?.status);
          if (fs && fs !== ps && PO_DETAIL_EDITABLE_STATUSES.includes(fs)) {
            payload.status = fs;
          }
        }
        await purchaseOrderService.update(id, payload);
        await loadDetail();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save purchase order');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [poForm, normalizedItems, isNew, isDraftPo, id, navigate, loadDetail, clearNewDraftFromStorage, po]);

  const saveRemotePurchaseOrderDraft = useCallback(async () => {
    if (isNew && newPoServerDraftSavingRef.current) return;
    if (isNew) newPoServerDraftSavingRef.current = true;
    try {
      setSavingDraft(true);
      setError('');
      const items = normalizedItems();
      if (isNew) {
        const res = await purchaseOrderService.create({
          is_draft: true,
          vendor_id: poForm.vendor_id ? Number(poForm.vendor_id) : null,
          ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
          work_order_id: poForm.work_order_id ? Number(poForm.work_order_id) : null,
          order_date: poForm.order_date,
          expected_date: poForm.expected_date || null,
          ship_via: poForm.ship_via || null,
          notes: poForm.notes || null,
          items
        });
        clearNewDraftFromStorage();
        const poData = res?.data;
        const newId = poData?.id ?? res?.id;
        navigatedAwayAfterServerDraftRef.current = true;
        flashNewPoDraftSaved();
        if (newId) {
          persistPoBootstrapSession(newId, poData);
          navigate(`/procurement/po/${newId}`, {
            replace: true,
            state: poData ? { wmNewPoBootstrap: poData } : {}
          });
        } else navigate('/procurement/po');
      } else if (isDraftPo) {
        const res = await purchaseOrderService.update(id, {
          po_number: poForm.po_number,
          vendor_id: poForm.vendor_id ? Number(poForm.vendor_id) : null,
          ordered_by_id: poForm.ordered_by_id ? Number(poForm.ordered_by_id) : null,
          work_order_id: poForm.work_order_id ? Number(poForm.work_order_id) : null,
          order_date: poForm.order_date,
          expected_date: poForm.expected_date || null,
          ship_via: poForm.ship_via || null,
          notes: poForm.notes || null,
          items,
          status: 'DRAFT'
        });
        const data = res?.data;
        if (data) setPo(data);
        // Avoid loadDetail() here: it toggles `loading` and remounts the whole form (feels like a reload)
        // on every debounced autosave. Baseline + header from what we just saved is enough.
        setPoDirtyBaseline(
          JSON.stringify({ poForm: poFormRef.current, lineItems: lineItemsRef.current })
        );
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save draft');
      throw err;
    } finally {
      if (isNew) newPoServerDraftSavingRef.current = false;
      setSavingDraft(false);
    }
  }, [poForm, normalizedItems, isNew, isDraftPo, id, navigate, loadDetail, clearNewDraftFromStorage, flashNewPoDraftSaved]);

  /** Always invoke latest draft saver (debounced vendor picker must not use a stale closure). */
  const saveRemotePurchaseOrderDraftRef = useRef(saveRemotePurchaseOrderDraft);
  useEffect(() => {
    saveRemotePurchaseOrderDraftRef.current = saveRemotePurchaseOrderDraft;
  }, [saveRemotePurchaseOrderDraft]);

  // New P.O.: persist a server draft when there is anything meaningful to store (vendor, notes, or
  // line content) so it appears on the PO list — not only localStorage. Debounced per edit.
  useEffect(() => {
    if (!isNew) return;
    if (!newPODraftHasServerPayload(poForm, lineItems)) return;
    const t = setTimeout(() => {
      saveRemotePurchaseOrderDraftRef.current().catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [
    isNew,
    poForm.vendor_id,
    poForm.notes,
    poForm.order_date,
    poForm.expected_date,
    poForm.ship_via,
    poForm.ordered_by_id,
    poForm.work_order_id,
    lineItems
  ]);

  // Existing DRAFT P.O.: keep server copy in sync while editing (tab switch / list rely on API).
  useEffect(() => {
    if (!isDraftPo || loading || poDirtyBaseline == null) return;
    if (JSON.stringify({ poForm, lineItems }) === poDirtyBaseline) return;
    const t = setTimeout(() => {
      saveRemotePurchaseOrderDraftRef.current().catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [isDraftPo, loading, poForm, lineItems, poDirtyBaseline]);

  // Browser tab hide / page unload: best-effort server flush (sidebar uses tryNavigate; this covers OS tab sleep).
  useEffect(() => {
    if (!isNew && !isDraftPo) return;
    const flushServer = () => {
      if (navigatedAwayAfterServerDraftRef.current) return;
      const form = poFormRef.current;
      const lines = lineItemsRef.current;
      if (isNew && !newPODraftHasServerPayload(form, lines)) return;
      void saveRemotePurchaseOrderDraftRef.current?.();
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') return;
      flushServer();
    };
    const onPageHide = () => {
      flushServer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [isNew, isDraftPo]);

  // Route unmount (e.g. in-app navigation): fire-and-forget server draft if still on unsaved /new data.
  useEffect(() => {
    return () => {
      if (navigatedAwayAfterServerDraftRef.current) return;
      const form = poFormRef.current;
      const lines = lineItemsRef.current;
      if (isNew && newPODraftHasServerPayload(form, lines)) {
        void saveRemotePurchaseOrderDraftRef.current?.();
      }
    };
  }, [isNew]);

  const handleSave = async () => {
    try {
      await persistPurchaseOrder();
    } catch {
      /* errors surfaced via setError */
    }
  };

  useEffect(() => {
    setPoDirtyBaseline(null);
    if (!isNew) return;
    newPoBaselineCapturedRef.current = false;
    setLastPersistedDraftSnapshot(null);
  }, [isNew]);

  const isPoDirtyForTabs = useMemo(() => {
    if (loading) return false;
    if (isNew) {
      if (lastPersistedDraftSnapshot == null) return false;
      return serializeNewPODraftState(poForm, lineItems) !== lastPersistedDraftSnapshot;
    }
    if (poDirtyBaseline == null) return false;
    return JSON.stringify({ poForm, lineItems }) !== poDirtyBaseline;
  }, [loading, isNew, poForm, lineItems, poDirtyBaseline, lastPersistedDraftSnapshot]);

  const discardPoTab = useCallback(() => {
    if (isNew) {
      setPoForm(emptyPOForm());
      setLineItems([emptyLineItem()]);
      setError('');
      setNewPoDraftSavedVisible(false);
      if (newPoDraftSavedFlashTimeoutRef.current) {
        clearTimeout(newPoDraftSavedFlashTimeoutRef.current);
        newPoDraftSavedFlashTimeoutRef.current = null;
      }
      clearNewDraftFromStorage();
      const blank = emptyPOForm();
      const blankLines = [emptyLineItem()];
      setLastPersistedDraftSnapshot(serializeNewPODraftState(blank, blankLines));
      newPoBaselineCapturedRef.current = true;
    } else {
      loadDetail();
    }
  }, [isNew, loadDetail, clearNewDraftFromStorage]);

  useEffect(() => {
    const path = location.pathname;
    if (isNew) {
      registerTabTitle(path, identifierPreview ? `P.O. (${identifierPreview})` : 'New P.O.');
    } else if (po) {
      const label = po.identifier || po.po_number;
      registerTabTitle(path, label ? `P.O. ${label}` : `P.O. ${id}`);
    }
  }, [location.pathname, isNew, po, id, identifierPreview, registerTabTitle]);

  useEffect(() => {
    const pathKey = location.pathname;
    return registerDirtyState(pathKey, {
      isDirty: () => isPoDirtyForTabs,
      save: async () => {
        if (isNew || isDraftPo) {
          await saveRemotePurchaseOrderDraft();
          return;
        }
        await persistPurchaseOrder();
      },
      discard: discardPoTab
    });
  }, [
    location.pathname,
    registerDirtyState,
    isPoDirtyForTabs,
    discardPoTab,
    persistPurchaseOrder,
    saveRemotePurchaseOrderDraft,
    isNew,
    isDraftPo
  ]);

  const handleCancelPO = async () => {
    if (isNew) return;
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.update(id, { status: 'CANCELLED' });
      setCancelConfirmOpen(false);
      await loadDetail();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel purchase order');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (v) => formatDateOnly(v, 'MM/dd/yyyy') || '—';

  const handleReceiveShipment = () => {
    if (!po) return;
    const poNum = po.po_number ?? po.identifier ?? '';
    navigate(`/procurement/shipments-in/new?po=${encodeURIComponent(poNum)}`);
  };

  const handleDelete = async () => {
    if (isNew) {
      void tryNavigate('/procurement/po');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await purchaseOrderService.delete(id);
      void tryNavigate('/procurement/po');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete purchase order');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
    }
  };

  const uploadAttachments = async (files) => {
    if (isNew || !po?.id || !files?.length) return;
    if (files.length > 5) return setAttachmentsError('You can upload a maximum of 5 files at a time.');
    const tooLarge = files.filter((f) => f.size > MAX_ATTACHMENT_FILE_SIZE_BYTES);
    if (tooLarge.length > 0) {
      return setAttachmentsError(`One or more files exceed the ${MAX_ATTACHMENT_FILE_SIZE_LABEL} size limit.`);
    }
    try {
      setAttachmentsError('');
      for (const file of files) {
        await attachmentService.upload({ file, purchaseOrderId: po.id });
      }
      await loadAttachments(po.id);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to upload attachment');
    }
  };

  const handleAttachmentDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOverAttachment(false);
    const fileList = event.dataTransfer?.files;
    if (!fileList || fileList.length === 0) return;
    await uploadAttachments(Array.from(fileList));
  };

  const handleDownloadAttachment = async (att) => {
    try {
      setAttachmentsError('');
      const res = await attachmentService.download(att.id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.file_name || `attachment-${att.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAttachmentsError(err.response?.data?.message || 'Failed to download attachment');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;
  }

  return (
    <>
      {!isNew && po && (
        <PrintPage
          title="Purchase Order"
          footer={
            <div className="print-footer">
              Generated on {format(new Date(), 'MM/dd/yyyy hh:mm a')}
            </div>
          }
        >
          <div className="print-quote-details-box print-section" style={{ marginBottom: 16 }}>
            <div className="print-quote-details-left">
              <div><strong>P.O. #:</strong> {po.identifier ?? po.po_number ?? '—'}</div>
              <div><strong>Order Date:</strong> {formatDate(po.order_date)}</div>
              <div><strong>Expected Date:</strong> {formatDate(po.expected_date)}</div>
              <div><strong>Ship Via:</strong> {po.ship_via || '—'}</div>
            </div>
            <div className="print-quote-details-right">
              <div>
                <strong>Receipt status:</strong> {getPoReceiptStatusChipProps(po.status).label}
              </div>
              <div><strong>Vendor:</strong> {po.vendor_name || '—'}</div>
              <div>
                <strong>Ordered By:</strong>{' '}
                {po.ordered_by_first_name || po.ordered_by_last_name
                  ? `${po.ordered_by_first_name || ''} ${po.ordered_by_last_name || ''}`.trim()
                  : '—'}
              </div>
            </div>
          </div>

          {!!po.notes && (
            <div className="print-section print-notes" style={{ marginBottom: 12 }}>
              <h3>Notes</h3>
              <p>{po.notes}</p>
            </div>
          )}

          <table className="print-table" style={{ width: '100%', marginBottom: 16 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Line</th>
                <th style={{ textAlign: 'left' }}>Part #</th>
                <th style={{ textAlign: 'left' }}>Description</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th style={{ textAlign: 'left' }}>Unit</th>
                <th style={{ textAlign: 'right' }}>Unit price</th>
                <th style={{ textAlign: 'right' }}>Line total</th>
              </tr>
            </thead>
            <tbody>
              {poLinesForPrint.length > 0 ? (
                poLinesForPrint.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td>{item.line_number ?? idx + 1}</td>
                    <td>{item.part_number || '—'}</td>
                    <td style={{ whiteSpace: 'pre-wrap' }}>
                      {item.description || '—'}
                      {item.notes ? `\n\nNotes: ${item.notes}` : ''}
                      {item.work_order_id != null ? `\n\nOrder ref: #${item.work_order_id}` : ''}
                    </td>
                    <td style={{ textAlign: 'right' }}>{item.quantity_ordered ?? 0}</td>
                    <td>{item.unit || 'EA'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {item.unit_price != null && item.unit_price !== ''
                        ? formatCurrency(Number(item.unit_price))
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(computePOLineExtended(item))}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center' }}>
                    No line items
                  </td>
                </tr>
              )}
            </tbody>
            {poLinesForPrint.length > 0 ? (
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ textAlign: 'right' }}>
                    Total
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(poPrintSubtotal)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </PrintPage>
      )}

      <Box className="no-print">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {isNew ? 'New Purchase Order' : `Purchase Order ${po?.identifier ?? po?.po_number ?? ''}`.trim()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => void tryNavigate('/procurement/po')}>Back</Button>
          {isNew && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  saveRemotePurchaseOrderDraft().catch(() => {});
                }}
                disabled={savingDraft || saving}
              >
                {savingDraft ? 'Saving draft…' : 'Save Draft'}
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setNewPoDraftSavedVisible(false);
                  if (newPoDraftSavedFlashTimeoutRef.current) {
                    clearTimeout(newPoDraftSavedFlashTimeoutRef.current);
                    newPoDraftSavedFlashTimeoutRef.current = null;
                  }
                  clearNewDraftFromStorage();
                  setPoForm(emptyPOForm());
                  setLineItems([emptyLineItem()]);
                  const blank = emptyPOForm();
                  const blankLines = [emptyLineItem()];
                  setLastPersistedDraftSnapshot(serializeNewPODraftState(blank, blankLines));
                  newPoBaselineCapturedRef.current = true;
                }}
              >
                Clear Draft
              </Button>
            </>
          )}
          {isDraftPo && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                saveRemotePurchaseOrderDraft().catch(() => {});
              }}
              disabled={savingDraft || saving}
            >
              {savingDraft ? 'Saving draft…' : 'Save Draft'}
            </Button>
          )}
          {canReceiveShipment && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<TextSnippet />}
              onClick={handleReceiveShipment}
            >
              Receive shipment
            </Button>
          )}
          {!isNew && (
            <Button variant="outlined" size="small" startIcon={<Print />} onClick={() => printWithFilename(poPrintFilename)} disabled={isDraftPo}>
              Print
            </Button>
          )}
          {!isNew && <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setDeleteConfirmOpen(true)}>Delete</Button>}
          {!isNew && po?.status !== 'CANCELLED' && (
            <Button variant="outlined" color="warning" size="small" onClick={() => setCancelConfirmOpen(true)} disabled={saving}>
              Cancel P.O.
            </Button>
          )}
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving || savingDraft}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>

      {isNew && newPoDraftSavedVisible && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          draft saved
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

      {isNew && (
        <ProcurementSummaryBar
          topBanner={orderLinkedPoTopBanner}
          secondaryRow={<ProcurementSummaryNotesDense text={poForm.notes} />}
        >
          <ProcurementSummaryField label="P.O. #">
            <Box sx={{ fontWeight: 600 }}>{identifierPreview || '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Vendor">
            {poForm.vendor_id ? (
              <Button
                size="small"
                variant="text"
                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                onClick={() => void tryNavigate(`/procurement/vendors/${poForm.vendor_id}`)}
              >
                {poForm.vendor_name ?? '—'}
              </Button>
            ) : (
              '—'
            )}
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Linked order">
            {poLinkedWorkOrderId ? (
              <Button
                size="small"
                variant="text"
                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                onClick={() => void tryNavigate(`/work-orders/${poLinkedWorkOrderId}`)}
              >
                {linkedJobRow && Number(linkedJobRow.id) === poLinkedWorkOrderId
                  ? linkedJobRow.identifier || linkedJobRow.work_order_number || `Order #${poLinkedWorkOrderId}`
                  : `Order #${poLinkedWorkOrderId}`}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Order Date">{formatDate(poForm.order_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Expected Date">{formatDate(poForm.expected_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Ship Via">{poForm.ship_via?.trim() ? poForm.ship_via : '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Receipt status">
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Shipments received" wide>
            <ShipmentsReceivedSummaryLinks receivers={[]} />
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      {!isNew && po && (
        <ProcurementSummaryBar
          topBanner={orderLinkedPoTopBanner}
          secondaryRow={<ProcurementSummaryNotesDense text={po.notes} />}
        >
          <ProcurementSummaryField label="P.O. #">
            <Box sx={{ fontWeight: 600 }}>{po.identifier ?? po.po_number ?? '—'}</Box>
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Vendor">
            {po.vendor_id ? (
              <Button
                size="small"
                variant="text"
                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                onClick={() => navigate(`/procurement/vendors/${po.vendor_id}`)}
              >
                {po.vendor_name ?? '—'}
              </Button>
            ) : (
              (po.vendor_name ?? '—')
            )}
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Linked order">
            {poLinkedWorkOrderId ? (
              <Button
                size="small"
                variant="text"
                sx={{ p: 0, minWidth: 'auto', textTransform: 'none', justifyContent: 'flex-start' }}
                onClick={() => navigate(`/work-orders/${poLinkedWorkOrderId}`)}
              >
                {linkedJobRow && Number(linkedJobRow.id) === poLinkedWorkOrderId
                  ? linkedJobRow.identifier || linkedJobRow.work_order_number || `Order #${poLinkedWorkOrderId}`
                  : `Order #${poLinkedWorkOrderId}`}
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Order Date">{formatDate(po.order_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Expected Date">{formatDate(po.expected_date)}</ProcurementSummaryField>
          <ProcurementSummaryField label="Ship Via">{po.ship_via ?? '—'}</ProcurementSummaryField>
          <ProcurementSummaryField label="Receipt status">
            <PoReceiptStatusChip status={po.status} />
          </ProcurementSummaryField>
          <ProcurementSummaryField label="Shipments received" wide>
            <ShipmentsReceivedSummaryLinks receivers={linkedReceivers} />
          </ProcurementSummaryField>
        </ProcurementSummaryBar>
      )}

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={vendors}
              getOptionLabel={(opt) => opt?.name ?? ''}
              value={vendors.find((v) => v.id === poForm.vendor_id) ?? null}
              onChange={(_, val) => setPoForm((f) => ({ ...f, vendor_id: val?.id ?? '', vendor_name: val?.name ?? '' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Vendor"
                  required={!isNew && !isDraftPo}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {showReceiptStatusSelect ? (
              <FormControl fullWidth size="small">
                <InputLabel id="po-detail-receipt-status-label">Receipt status</InputLabel>
                <Select
                  labelId="po-detail-receipt-status-label"
                  label="Receipt status"
                  value={(() => {
                    const serverSt = normPoStatus(po?.status);
                    const formSt = normPoStatus(poForm.status);
                    if (PO_DETAIL_EDITABLE_STATUSES.includes(formSt)) return formSt;
                    if (PO_DETAIL_EDITABLE_STATUSES.includes(serverSt)) return serverSt;
                    return 'ORDERED';
                  })()}
                  onChange={(e) =>
                    setPoForm((f) => ({ ...f, status: normPoStatus(e.target.value) || 'ORDERED' }))
                  }
                >
                  {PO_DETAIL_EDITABLE_STATUSES.map((st) => (
                    <MenuItem key={st} value={st}>
                      {getPoReceiptStatusChipProps(st).label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="P.O. #"
                value={isNew ? identifierPreview : (po?.identifier ?? poForm.po_number ?? '')}
                InputProps={{ readOnly: true }}
                helperText={
                  isNew
                    ? 'Assigned on save (preview updates when order date or order-linked lines change).'
                    : ''
                }
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            {isNew || normPoStatus(po?.status) !== 'CANCELLED' ? (
              <Autocomplete
                options={jobSearchOptions}
                loading={jobSearchLoading}
                loadingText="Searching…"
                filterOptions={(opts) => opts}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                value={
                  linkedJobRow &&
                  poLinkedWorkOrderId &&
                  Number(linkedJobRow.id) === poLinkedWorkOrderId
                    ? linkedJobRow
                    : null
                }
                getOptionLabel={(opt) =>
                  opt
                    ? `${opt.identifier || opt.work_order_number || `#${opt.id}`}${
                        opt.company_name ? ` · ${opt.company_name}` : ''
                      }`.trim()
                    : ''
                }
                onInputChange={(_, input, reason) => {
                  if (jobSearchTimerRef.current) clearTimeout(jobSearchTimerRef.current);
                  if (reason !== 'input') return;
                  const q = (input || '').trim();
                  if (q.length < 2) {
                    setJobSearchOptions([]);
                    return;
                  }
                  jobSearchTimerRef.current = setTimeout(async () => {
                    setJobSearchLoading(true);
                    try {
                      const res = await orderService.getAll({ search: q, limit: 25 });
                      const rows = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
                      setJobSearchOptions(rows);
                    } catch {
                      setJobSearchOptions([]);
                    } finally {
                      setJobSearchLoading(false);
                    }
                  }, 300);
                }}
                onChange={(_, val) => {
                  setPoForm((f) => ({ ...f, work_order_id: val?.id != null ? val.id : '' }));
                  setLinkedJobRow(val || null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Job / order (optional)"
                    helperText="Cost/job bucket. Search by order # or customer. Lines can still link to order line items."
                    InputLabelProps={{ shrink: true }}
                  />
                )}
                size="small"
              />
            ) : poForm.work_order_id ? (
              <TextField
                fullWidth
                size="small"
                label="Job / order"
                value={
                  linkedJobRow
                    ? `${linkedJobRow.identifier || linkedJobRow.work_order_number || `#${linkedJobRow.id}`}`
                    : `Order #${poForm.work_order_id}`
                }
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                onClick={() => navigate(`/work-orders/${poForm.work_order_id}`)}
                sx={{
                  '& .MuiInputBase-input': { cursor: 'pointer' },
                  '& .MuiOutlinedInput-root': { cursor: 'pointer' }
                }}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Job / order"
                value="—"
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {showReceiptStatusSelect ? (
              <TextField
                fullWidth
                size="small"
                label="P.O. #"
                value={po?.identifier ?? poForm.po_number ?? ''}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%', '& > *': { flex: 1, minWidth: 0 } }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order date"
                  type="date"
                  value={poForm.order_date}
                  onChange={(e) => setPoForm((f) => ({ ...f, order_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Expected date"
                  type="date"
                  value={poForm.expected_date}
                  onChange={(e) => setPoForm((f) => ({ ...f, expected_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={employees}
              getOptionLabel={(opt) => opt ? `${opt.first_name || ''} ${opt.last_name || ''}`.trim() || opt.email || '—' : ''}
              value={employees.find((e) => e.id === poForm.ordered_by_id) ?? null}
              onChange={(_, val) => setPoForm((f) => ({ ...f, ordered_by_id: val?.id ?? '' }))}
              renderInput={(params) => <TextField {...params} size="small" label="Ordered By" InputLabelProps={{ shrink: true }} />}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              select
              label="Ship via"
              value={poForm.ship_via || ''}
              onChange={(e) => setPoForm((f) => ({ ...f, ship_via: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {SHIP_VIA_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {showReceiptStatusSelect && (
            <Grid item xs={12}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order date"
                  type="date"
                  value={poForm.order_date}
                  onChange={(e) => setPoForm((f) => ({ ...f, order_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Expected date"
                  type="date"
                  value={poForm.expected_date}
                  onChange={(e) => setPoForm((f) => ({ ...f, expected_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }}
                />
              </Stack>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              multiline
              minRows={2}
              value={poForm.notes}
              onChange={(e) => setPoForm((f) => ({ ...f, notes: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {isOrderLinkedPo && (
        <Alert severity="info" variant="outlined" icon={false} sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            <strong>Billing path:</strong> order-linked POs support the <strong>Order → Create invoice</strong> (customer
            invoice) flow. They are separate from the default procurement story of matching this PO to vendor invoices via
            received shipments.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.75 }}>
          <Typography variant="subtitle2">Line items</Typography>
          <Button variant="contained" size="small" startIcon={<Save />} onClick={handleSave} disabled={saving || savingDraft}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="caption">Part #</Typography></TableCell>
                <TableCell><Typography variant="caption">Description</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Qty</Typography></TableCell>
                <TableCell><Typography variant="caption">Unit</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Unit price</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption">Line total</Typography></TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lineItems.map((it, idx) => (
                <TableRow key={it.id != null ? `po-line-${it.id}` : `po-line-new-${idx}`} sx={{ '& td': { verticalAlign: 'top' } }}>
                  <TableCell><TextField size="small" value={it.part_number} onChange={(e) => updateLineItem(idx, 'part_number', e.target.value)} sx={{ width: 120 }} /></TableCell>
                  <TableCell sx={{ minWidth: 160, maxWidth: 360 }}>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      value={it.description}
                      onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                    />
                  </TableCell>
                  <TableCell align="right"><TextField type="number" size="small" value={it.quantity_ordered ?? ''} onChange={(e) => updateLineItem(idx, 'quantity_ordered', e.target.value)} sx={{ width: 90 }} /></TableCell>
                  <TableCell><TextField size="small" value={it.unit} onChange={(e) => updateLineItem(idx, 'unit', e.target.value)} sx={{ width: 70 }} /></TableCell>
                  <TableCell align="right"><TextField type="number" size="small" value={it.unit_price ?? ''} onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)} sx={{ width: 110 }} /></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ pr: 0.5, whiteSpace: 'nowrap', pt: 1 }}>
                      {formatCurrency(computePOLineExtended(it))}
                    </Typography>
                  </TableCell>
                  <TableCell><IconButton size="small" onClick={() => removeLineItem(idx)} disabled={lineItems.length <= 1}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={5} align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                  <Typography variant="subtitle2">Total</Typography>
                </TableCell>
                <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 600 }}>
                  <Typography variant="subtitle2">{formatCurrency(computePOLinesSubtotal(lineItems))}</Typography>
                </TableCell>
                <TableCell sx={{ borderBottom: 'none' }} />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
        <Button size="small" startIcon={<AddIcon />} onClick={addLineItem} sx={{ mt: 0.75 }}>Add line</Button>
      </Paper>

      {!isNew && (
        <Paper sx={{ p: 1.5, mb: 1.5 }}>
          <Box
            onDrop={handleAttachmentDrop}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingOverAttachment(true); }}
            onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingOverAttachment(false); }}
            sx={{
              p: 2, mb: 1, minHeight: 120, border: '2px dashed',
              borderColor: isDraggingOverAttachment ? 'primary.main' : 'grey.400',
              borderRadius: 2, bgcolor: isDraggingOverAttachment ? 'action.hover' : 'grey.50',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Drop documents for PO {po?.identifier ?? po?.po_number}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>or use Upload · max 5 files, {MAX_ATTACHMENT_FILE_SIZE_LABEL} each</Typography>
            </Box>
            <Button variant="contained" component="label" size="small" startIcon={<CloudUpload />}>
              Upload
              <input type="file" hidden multiple onChange={async (e) => {
                const files = e.target.files;
                if (files?.length) await uploadAttachments(Array.from(files));
                e.target.value = '';
              }} />
            </Button>
          </Box>
          {attachmentsError && <Alert severity="error" sx={{ mb: 1 }}>{attachmentsError}</Alert>}
          {attachmentsLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={24} /></Box> : (
            attachments.length === 0 ? <Typography variant="body2" color="text.secondary">No attachments yet.</Typography> : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><Typography variant="caption">File Name</Typography></TableCell>
                      <TableCell><Typography variant="caption">Size</Typography></TableCell>
                      <TableCell><Typography variant="caption">Uploaded At</Typography></TableCell>
                      <TableCell><Typography variant="caption">Categories</Typography></TableCell>
                      <TableCell width={80}><Typography variant="caption">Actions</Typography></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachments.map((att) => (
                      <TableRow key={att.id} hover>
                        <TableCell><Button size="small" sx={{ textTransform: 'none' }} onClick={() => handleDownloadAttachment(att)}>{att.file_name}</Button></TableCell>
                        <TableCell>{att.file_size != null ? `${(att.file_size / 1024 / 1024).toFixed(2)} MB` : '—'}</TableCell>
                        <TableCell>{att.uploaded_at ? format(new Date(att.uploaded_at), 'MM/dd/yyyy HH:mm') : '—'}</TableCell>
                        <TableCell>
                          <AttachmentCategoryChips
                            attachmentId={att.id}
                            categories={att.categories}
                            onUpdated={(row) =>
                              setAttachments((prev) =>
                                prev.map((a) => (a.id === row.id ? { ...a, ...row } : a))
                              )
                            }
                            onError={(msg) => setAttachmentsError(msg)}
                          />
                        </TableCell>
                        <TableCell><IconButton size="small" color="error" onClick={async () => {
                          try {
                            await attachmentService.delete(att.id);
                            setAttachments((prev) => prev.filter((a) => a.id !== att.id));
                          } catch (err) {
                            setAttachmentsError(err.response?.data?.message || 'Failed to delete attachment');
                          }
                        }}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
        </Paper>
      )}

      <Dialog open={cancelConfirmOpen} onClose={() => !saving && setCancelConfirmOpen(false)}>
        <DialogTitle>Cancel Purchase Order</DialogTitle>
        <DialogContent>
          <Typography>
            Mark P.O. <strong>{po?.identifier ?? po?.po_number ?? id}</strong> as <strong>CANCELLED</strong>? Receipt status will no longer update from shipments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} disabled={saving}>
            Back
          </Button>
          <Button variant="contained" color="warning" onClick={handleCancelPO} disabled={saving}>
            {saving ? 'Saving…' : 'Cancel P.O.'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !saving && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Purchase Order?</DialogTitle>
        <DialogContent>
          Delete <strong>{po?.identifier ?? po?.po_number ?? id}</strong>? This cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={saving}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
};

export default ProcurementPODetail;

